import { Box, CircularProgress, Container } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import lodash from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ContentHubTrendCard from "./components/ContentHubTrendCard";
import OmneInsightsModal from "./components/OmneInsightsModal";
import { useGetContentHubTrendReport, useGetCountryListOptions } from "../../api/leadsManagement";
import DateField from "../../components/atoms/DateField";
import MultiSelect from "../../components/atoms/MultiSelect";
import SelectField from "../../components/atoms/SelectField";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { usePermissionStore } from "../../stores/permissionStore";
import ErrorBoundaryPage from "../error/ErrorBoundaryPage";

type FormValues = {
  startDate: string | null;
  endDate: string | null;
  group_criteria: string[];
  country: string | null;
};

type ContentHubTrendItem = {
  main_topic: string;
  sub_topic: string;
  num_of_articles: number;
  view_count: number;
  top_5_countries: string;
  mode_agerange: string;
  Female: number;
  Male: number;
  PreferNotToDisclose: number;
  average_viewed_percentage: number;
  average_viewed_duration_sec: number;
};

interface SocialInsightDisplay {
  category: string;
  views: number;
  posts: number;
  dataInsight: string;
  audienceTrend: string;
  action: string;
  relevanceScore: string;
  relevanceAssessment: string;
  num_of_articles?: number;
  view_count?: number;
  top_5_countries?: string;
  mode_agerange?: string;
  Female?: number;
  Male?: number;
  PreferNotToDisclose?: number;
  average_viewed_percentage?: number;
  average_viewed_duration_sec?: number;
}

const OmneInsights = () => {
  const { t } = useTranslation();
  const permissionToken = usePermissionStore.getState().permissionToken ?? "";

  const formMethods = useForm<FormValues>({
    defaultValues: {
      startDate: dayjs().subtract(30, "day").format("DD/MM/YYYY 00:00:00"),
      endDate: dayjs().format("DD/MM/YYYY 00:00:00"),
      group_criteria: [],
      country: null,
    },
  });
  const { watch } = formMethods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const selectedGroupCriteria = watch("group_criteria") ?? [];
  const selectedCountry = watch("country");

  const { data: countryResp } = useGetCountryListOptions({ permission_Token: permissionToken });
  const countryOptions = useMemo(
    () =>
      (countryResp?.data?.options ?? []).map((opt: { code: string; description: string }) => ({
        label: opt.description,
        value: opt.code,
      })),
    [countryResp]
  );

  const groupCriteriaOptions = useMemo(
    () => [
      { label: "Main Topic", value: "main_topic" },
      { label: "Sub Topic", value: "sub_topic" },
    ],
    []
  );

  const countryMap = useMemo(() => {
    const map = new Map<string, string>();
    (countryResp?.data?.options ?? []).forEach((o: { code: string; description: string }) =>
      map.set(o.code, o.description)
    );
    return map;
  }, [countryResp]);

  const {
    data: trendResp,
    isLoading: isLoadingTrend,
    isError: isTrendError,
    refetch: refetchTrend,
  } = useGetContentHubTrendReport(
    {
      permission_Token: permissionToken,
      start_date: startDate ? dayjs(startDate, "DD/MM/YYYY HH:mm:ss").format("YYYY-MM-DDT00:00:00") : "",
      end_date: endDate ? dayjs(endDate, "DD/MM/YYYY HH:mm:ss").format("YYYY-MM-DDT00:00:00") : "",
      ...(selectedGroupCriteria && selectedGroupCriteria.length ? { group_criteria: selectedGroupCriteria } : {}),
      ...(selectedCountry ? { country: [selectedCountry] } : {}),
    },
    {
      enabled: Boolean(startDate && endDate && permissionToken),
    }
  );

  useEffect(() => {
    if (startDate && endDate && permissionToken) refetchTrend();
  }, [startDate, endDate, selectedGroupCriteria, selectedCountry, permissionToken, refetchTrend]);

  const items: ContentHubTrendItem[] = (trendResp?.data?.data ?? []) as any;

  type Grouped = {
    mainTopic: string;
    subCount: number;
    subItems: ContentHubTrendItem[];
  };

  const groupedByMainTopic: Grouped[] = useMemo(() => {
    const map = new Map<string, ContentHubTrendItem[]>();
    items.forEach((it) => {
      const arr = map.get(it.main_topic) ?? [];
      arr.push(it);
      map.set(it.main_topic, arr);
    });

    return Array.from(map.entries()).map(([mainTopic, subItems]) => ({
      mainTopic,
      subCount: subItems.length,
      subItems,
    }));
  }, [items]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMainTopic, setSelectedMainTopic] = useState<string | null>(null);
  const [selectedSubDisplays, setSelectedSubDisplays] = useState<SocialInsightDisplay[]>([]);

  const mapSubItemToDisplay = (it: ContentHubTrendItem): SocialInsightDisplay => ({
    category: it.sub_topic,
    views: it.view_count,
    posts: it.num_of_articles,
    dataInsight: "",
    audienceTrend: "",
    action: "",
    relevanceScore: "",
    relevanceAssessment: "",
    num_of_articles: it.num_of_articles,
    view_count: it.view_count,
    top_5_countries: it.top_5_countries,
    mode_agerange: it.mode_agerange,
    Female: it.Female,
    Male: it.Male,
    PreferNotToDisclose: it.PreferNotToDisclose,
    average_viewed_percentage: it.average_viewed_percentage,
    average_viewed_duration_sec: it.average_viewed_duration_sec,
  });

  const handleOpenModal = (group: Grouped) => {
    setSelectedMainTopic(group.mainTopic);
    setSelectedSubDisplays(group.subItems.map(mapSubItemToDisplay));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMainTopic(null);
    setSelectedSubDisplays([]);
  };

  return (
    <ErrorBoundary fallback={<ErrorBoundaryPage />}>
      <Container
        maxWidth={false}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flex: 1,
          height: "100%",
          marginBottom: "50px",
          maxWidth: "1800px",
        }}
      >
        <FormProvider {...formMethods}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                gap: 2,
                mt: "32px",
                mb: "16px",
              }}
            >
              <Box>
                <DateField name="startDate" label={t("platform.startDate")} maxDate />
              </Box>
              <Box>
                <DateField name="endDate" label={t("platform.endDate")} maxDate dateNotBefore={"startDate"} />
              </Box>
              <Box>
                <MultiSelect selectWidth="100%" options={groupCriteriaOptions} name="group_criteria" label="Group Criteria" />
              </Box>
              <Box>
                <SelectField options={countryOptions ?? []} name="country" label={t("platform.country")} />
              </Box>
            </Box>
          </LocalizationProvider>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(auto-fit, minmax(280px, 350px))",
                sm: "repeat(auto-fit, minmax(280px, 350px))",
                md: "repeat(3, 1fr)",
                "@media (min-width: 2000px)": { gridTemplateColumns: "repeat(auto-fit, minmax(280px, 350px))" },
              },
              gap: { xs: 2, sm: 3, md: 3 },
              maxWidth: "1800px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {isLoadingTrend ? (
              <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", alignItems: "center", height: "150px" }}>
                <CircularProgress size={24} />
              </Box>
            ) : isTrendError ? (
              <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", alignItems: "center", height: "150px" }}>
                Error loading data.
              </Box>
            ) : (
              lodash.orderBy(groupedByMainTopic, ["subCount"], ["desc"]).map((group, idx) => (
                <Box key={`${group.mainTopic}-${idx}`} sx={{ marginBottom: idx === groupedByMainTopic.length - 1 ? "36px" : 0 }}>
                  <ContentHubTrendCard
                    title={group.mainTopic}
                    mode="compact"
                    countLabel="Sub Topics"
                    countValue={group.subCount}
                    onClick={() => handleOpenModal(group)}
                  />
                </Box>
              ))
            )}
          </Box>

          <OmneInsightsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            title={selectedMainTopic ?? ""}
            categoryDataList={selectedSubDisplays}
          />
        </FormProvider>
      </Container>
    </ErrorBoundary>
  );
};

export default OmneInsights;